"use client";

import { ErrorBoundary, useErrorBoundary } from "react-error-boundary";
import React, { Suspense } from "react";

import Header from "components/Header";
import { ThemeProviderTme } from "styles/ThemeProviderTme";

import { AppContainer, PageContainer } from "./styles";
import AppRoutes from "./Routes";
import OktaSecurity from "components/OktaSecurity";
import { useSelector } from "react-redux";

import { datadogRum } from "@datadog/browser-rum";
import { CircularProgress } from "@mui/material";
import Alert from "components/Alert";

import configureStore from "redux/configureStore";
import { AppInsightsErrorBoundary } from '@microsoft/applicationinsights-react-js';
import { reactPlugin } from 'shared/utils/helperFunctions/appInsights';

const { persistor } = configureStore();

const { env } = window.jllConfig || "local";

datadogRum.init({
  applicationId: "a0e71879-4637-4026-bc76-580d776e5f5e",
  clientToken: "pub2d9c39c98afd427adf00f8bfb9145d5d",
  site: "us3.datadoghq.com",
  service: "global-tm-tracker",
  env: env,
  version: "1.0.0", // localStorage.getItem("ui-version") || "1.0.0",
  sessionSampleRate: 100,
  sessionReplaySampleRate: 0,
  trackUserInteractions: true,
  trackResources: true,
  trackLongTasks: true,
  defaultPrivacyLevel: "mask-user-input",
});

function ErrorFallbackHeader({ error }) {
  // Call resetErrorBoundary() to reset the error boundary and retry the render.
  const { resetBoundary } = useErrorBoundary();

  return (
    <Alert error={"Something went wrong. Please try again."} displaySupportLink={true} />
  );
}

function ErrorFallback({ error }) {
  // Call resetErrorBoundary() to reset the error boundary and retry the render.
  const { resetBoundary } = useErrorBoundary();

  return (
    <Alert error={"Something went wrong. Please try again."} displaySupportLink={true} />
  );
}

const App = () => {
  const user = useSelector((state) => state.currentUser.details);

  if (user) {
    const { id, userName, userEmailAddress } = user;

    datadogRum.setUser({
      id,
      name: userName,
      email: userEmailAddress,
    });
  }

  const hardRefresh = () => {
    persistor.pause();
    persistor.flush().then(() => {
      return persistor.purge();
    }).finally(() => {
      window.location.reload();
    });
  }

  const logError = (error, info) => {
    const renderingError = new Error(error.message);

    renderingError.name = `ReactRenderingError`;
    renderingError.stack = info.componentStack;
    renderingError.cause = error;

    datadogRum.addError(renderingError);

    if (
      error?.message &&
      (error.message.toLowerCase().includes("transaction") ||
        error.message.toLowerCase().includes("client"))
    ) {
      persistor.pause();
      persistor.flush().then(() => {
        return persistor.purge();
      });
    }
     
    console.error(error, info);
  };

  return (
    <AppInsightsErrorBoundary reactPlugin={reactPlugin} onError={() => <h1>Error!</h1>}>    
      <OktaSecurity>
        <AppContainer>
          <ErrorBoundary
            FallbackComponent={ErrorFallbackHeader}
            onError={logError}
            onReset={hardRefresh}
          >
            <Header />
          </ErrorBoundary>
          <PageContainer>
            <ThemeProviderTme>
              <Suspense fallback={<CircularProgress />}>
                <ErrorBoundary
                  FallbackComponent={ErrorFallback}
                  onError={logError}
                  onReset={hardRefresh}
                >
                  <AppRoutes />
                </ErrorBoundary>
              </Suspense>
            </ThemeProviderTme>
          </PageContainer>
        </AppContainer>
      </OktaSecurity>
    </AppInsightsErrorBoundary>
  );
};

export default App;
